"use client";

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  usersGetUserProjects,
  organizationsGetOrganizationProjects,
  projectsCreateProject,
  projectsUpdateProject,
  projectsDeleteProject,
  projectsGetProject,
  projectsGetProjectFiles,
  projectsGetProjectThreads,
  filesUploadFile,
  filesDeleteFile,
  filesScrapeWebPage,
  filesIndexFile,
} from "../sdk";
import { client } from "../sdk/client.gen";
import type {
  PersistentProject,
  CreateProjectRequest,
  UpdateProjectRequest,
  PersistentFile,
} from "../sdk";
import { queryKeys } from "../query/keys";
import { LOW_CHURN_STALE_TIME, GC_TIME } from "../query/config";

// Re-export SDK types for consumers
export type {
  PersistentProject,
  CreateProjectRequest,
  UpdateProjectRequest,
  PersistentFile,
};

export type ProjectVisibility = "project_members" | "org_members";
export type ProjectRole = "OWNER" | "ADMIN" | "MEMBER" | "VIEWER";

export type ProjectWithVisibility = PersistentProject & {
  visibility?: ProjectVisibility;
};

export type ProjectMember = {
  id?: string | null;
  created_at: string;
  edited_at: string;
  project_id: string;
  user_id: string;
  role: ProjectRole;
  added_by?: string | null;
};

export type CreateProjectInput = CreateProjectRequest & {
  visibility?: ProjectVisibility;
  member_ids?: string[];
};

/**
 * Fetch projects for a user with optional org filtering.
 *
 * Uses React Query for caching - returns cached data instantly on navigation back.
 *
 * @param options - userId (required), orgId, enabled
 * @returns React Query result with projects data
 */
export function useProjects(options: {
  userId: string;
  orgId?: string;
  enabled?: boolean;
}) {
  const { userId, orgId, enabled = true } = options;

  return useQuery({
    queryKey: queryKeys.projects.list({ userId, orgId }),
    queryFn: async () => {
      if (orgId) {
        const response = await organizationsGetOrganizationProjects({
          path: { org_id: orgId },
        });
        return (response.data ?? []) as ProjectWithVisibility[];
      } else {
        const response = await usersGetUserProjects({
          path: { user_id: userId },
        });
        const data = response.data;
        return (data && "results" in data ? data.results : []) as ProjectWithVisibility[];
      }
    },
    staleTime: LOW_CHURN_STALE_TIME,
    gcTime: GC_TIME,
    enabled: enabled && !!userId,
  });
}

/**
 * Fetch a single project by ID.
 *
 * @param projectId - Project ID
 * @param options - Additional options
 * @returns React Query result with project data
 */
export function useProject(projectId: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.projects.detail(projectId),
    queryFn: async () => {
      const response = await projectsGetProject({
        path: { project_id: projectId },
      });
      return (response.data ?? null) as ProjectWithVisibility | null;
    },
    staleTime: LOW_CHURN_STALE_TIME,
    gcTime: GC_TIME,
    enabled: options?.enabled ?? !!projectId,
  });
}

/**
 * Fetch files for a project.
 *
 * @param projectId - Project ID
 * @param options - Additional options
 * @returns React Query result with project files
 */
export function useProjectFiles(
  projectId: string,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: queryKeys.projects.files(projectId),
    queryFn: async () => {
      const response = await projectsGetProjectFiles({
        path: { project_id: projectId },
      });
      return response.data ?? [];
    },
    staleTime: LOW_CHURN_STALE_TIME,
    gcTime: GC_TIME,
    enabled: options?.enabled ?? !!projectId,
  });
}

/**
 * Fetch threads for a project.
 *
 * @param projectId - Project ID
 * @param options - Additional options
 * @returns React Query result with project threads
 */
export function useProjectThreads(
  projectId: string,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: queryKeys.projects.threads(projectId),
    queryFn: async () => {
      const response = await projectsGetProjectThreads({
        path: { project_id: projectId },
      });
      return response.data ?? [];
    },
    staleTime: LOW_CHURN_STALE_TIME,
    gcTime: GC_TIME,
    enabled: options?.enabled ?? !!projectId,
  });
}

/**
 * Create a new project.
 *
 * Optimistically adds to cache on success.
 */
export function useCreateProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateProjectInput) => {
      const response = await projectsCreateProject({ body: data });
      if (!response.data) throw new Error("Failed to create project");
      return response.data;
    },
    onSuccess: (newProject, variables) => {
      if (!newProject.id) return;
      const ownerId = variables.user_id ?? newProject.user_id;
      if (!ownerId) return;
      // Add to list cache
      queryClient.setQueryData<PersistentProject[]>(
        queryKeys.projects.list({
          userId: ownerId,
          orgId: variables.org_id ?? undefined,
        }),
        (old) => [...(old ?? []), newProject]
      );
      // Also set detail cache
      queryClient.setQueryData(
        queryKeys.projects.detail(newProject.id),
        newProject
      );
    },
  });
}

/**
 * Update an existing project.
 *
 * Updates both detail and list caches.
 */
export function useUpdateProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      projectId,
      data,
    }: {
      projectId: string;
      data: UpdateProjectRequest;
      userId: string;
      orgId?: string;
    }) => {
      const response = await projectsUpdateProject({
        path: { project_id: projectId },
        body: data,
      });
      if (!response.data) throw new Error("Failed to update project");
      return response.data;
    },
    onSuccess: (updatedProject, variables) => {
      // Update detail cache
      queryClient.setQueryData(
        queryKeys.projects.detail(variables.projectId),
        updatedProject
      );
      // Update in list cache
      queryClient.setQueryData<PersistentProject[]>(
        queryKeys.projects.list({
          userId: variables.userId,
          orgId: variables.orgId,
        }),
        (old) =>
          (old ?? []).map((p) =>
            p.id === variables.projectId ? updatedProject : p
          )
      );
    },
  });
}

/**
 * Delete a project.
 *
 * Optimistically removes from cache, rolls back on error.
 */
export function useDeleteProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      projectId,
    }: {
      projectId: string;
      userId: string;
      orgId?: string;
    }) => {
      await projectsDeleteProject({ path: { project_id: projectId } });
      return projectId;
    },
    onMutate: async ({ projectId, userId, orgId }) => {
      const listKey = queryKeys.projects.list({ userId, orgId });

      // Cancel in-flight queries
      await queryClient.cancelQueries({ queryKey: listKey });

      // Snapshot current data for rollback
      const previous = queryClient.getQueryData<PersistentProject[]>(listKey);

      // Optimistically remove from cache
      queryClient.setQueryData<PersistentProject[]>(listKey, (old) =>
        (old ?? []).filter((p) => p.id !== projectId)
      );

      // Remove detail cache
      queryClient.removeQueries({
        queryKey: queryKeys.projects.detail(projectId),
      });

      return { previous, listKey };
    },
    onError: (_err, _variables, context) => {
      // Rollback on error
      if (context?.previous) {
        queryClient.setQueryData(context.listKey, context.previous);
      }
    },
  });
}

/**
 * Upload a file to a project and invalidate the files cache.
 */
export function useUploadFile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      file,
      projectId,
    }: {
      file: File;
      projectId: string;
    }) => {
      await filesUploadFile({
        path: { project_id: projectId },
        body: { file },
      });
    },
    onSuccess: (_data, { projectId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.projects.files(projectId),
      });
    },
  });
}

/**
 * Delete a file and optimistically remove from cache.
 */
export function useDeleteFile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      fileId,
    }: {
      fileId: string;
      projectId: string;
    }) => {
      await filesDeleteFile({ path: { file_id: fileId } });
      return fileId;
    },
    onSuccess: (fileId, { projectId }) => {
      queryClient.setQueryData<PersistentFile[]>(
        queryKeys.projects.files(projectId),
        (old) => (old ?? []).filter((f) => f.id !== fileId)
      );
    },
  });
}

/**
 * Scrape a web page and add it as a project file.
 */
export function useScrapeWebPage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      link,
      projectId,
    }: {
      link: string;
      projectId: string;
    }) => {
      await filesScrapeWebPage({
        path: { project_id: projectId },
        query: { url: link },
      });
    },
    onSuccess: (_data, { projectId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.projects.files(projectId),
      });
    },
  });
}

export function useProjectMembers(
  projectId: string,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: [...queryKeys.projects.detail(projectId), "members"] as const,
    queryFn: async () => {
      const response = await client.get({
        security: [{ scheme: "bearer", type: "http" }],
        url: "/api/projects/{project_id}/members",
        path: { project_id: projectId },
      });
      return (response.data ?? []) as ProjectMember[];
    },
    staleTime: LOW_CHURN_STALE_TIME,
    gcTime: GC_TIME,
    enabled: options?.enabled ?? !!projectId,
  });
}

export function useAddProjectMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      projectId,
      userId,
      role = "MEMBER",
    }: {
      projectId: string;
      userId: string;
      role?: ProjectRole;
    }) => {
      const response = await client.post({
        security: [{ scheme: "bearer", type: "http" }],
        url: "/api/projects/{project_id}/members",
        path: { project_id: projectId },
        body: { user_id: userId, role },
      });
      return response.data as ProjectMember;
    },
    onSuccess: (_data, { projectId }) => {
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.projects.detail(projectId), "members"],
      });
    },
  });
}

export function useUpdateProjectMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      projectId,
      userId,
      role,
    }: {
      projectId: string;
      userId: string;
      role: ProjectRole;
    }) => {
      const response = await client.put({
        security: [{ scheme: "bearer", type: "http" }],
        url: "/api/projects/{project_id}/members/{member_user_id}",
        path: { project_id: projectId, member_user_id: userId },
        body: { role },
      });
      return response.data as ProjectMember;
    },
    onSuccess: (_data, { projectId }) => {
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.projects.detail(projectId), "members"],
      });
    },
  });
}

export function useRemoveProjectMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      projectId,
      userId,
    }: {
      projectId: string;
      userId: string;
    }) => {
      await client.delete({
        security: [{ scheme: "bearer", type: "http" }],
        url: "/api/projects/{project_id}/members/{member_user_id}",
        path: { project_id: projectId, member_user_id: userId },
      });
    },
    onSuccess: (_data, { projectId }) => {
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.projects.detail(projectId), "members"],
      });
    },
  });
}

/**
 * Index unindexed files in batches.
 *
 * Accepts the current files list and processes unindexed files in batches of 3.
 * Invalidates the files cache after each batch.
 */
export function useIndexUnindexedFiles() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      projectId,
      files,
    }: {
      projectId: string;
      files: PersistentFile[];
    }) => {
      let unindexedFiles = files.filter(
        (file) => !file.file_indexed && file.index_status !== "PROCESSING"
      );
      let updatedCount = 0;
      let failedCount = 0;
      const failedFiles: string[] = [];

      while (unindexedFiles.length > 0) {
        const batch = unindexedFiles.slice(0, 3);

        const updateResults = await Promise.allSettled(
          batch.map(async (file) => {
            try {
              await filesIndexFile({ path: { file_id: file.id! } });
              return { success: true, fileId: file.id };
            } catch (error) {
              console.error(`Failed to index file ${file.id}:`, error);
              return { success: false, fileId: file.id, error };
            }
          })
        );

        updateResults.forEach((result) => {
          if (result.status === "fulfilled" && result.value.success) {
            updatedCount++;
          } else {
            failedCount++;
            if (
              result.status === "fulfilled" &&
              !result.value.success &&
              result.value.fileId
            ) {
              failedFiles.push(result.value.fileId);
            }
          }
        });

        // Refetch files list to get updated statuses
        await queryClient.invalidateQueries({
          queryKey: queryKeys.projects.files(projectId),
        });

        // Get updated files from cache
        const updatedFiles =
          queryClient.getQueryData<PersistentFile[]>(
            queryKeys.projects.files(projectId)
          ) ?? [];

        unindexedFiles = updatedFiles.filter(
          (file) =>
            !file.file_indexed &&
            file.index_status !== "PROCESSING" &&
            !failedFiles.includes(file.id!)
        );
      }

      return { updatedCount, failedCount };
    },
    onSuccess: (_data, { projectId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.projects.files(projectId),
      });
    },
  });
}
